import { AppState } from 'react-native';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

export const MAX_CANNED_QUESTIONS = 10;

export enum CannedQuestionCategory {
  Fulfillment,
  OrderDate,
  OrderPrice,
  SalesAlert,
  Search,
}

enum QuestionDataRequirement {
  Item,
  Customer,
  Credit,
}

export interface CannedQuestion {
  _id: number;
  category: CannedQuestionCategory;
  dataRequirement?: Record<string, QuestionDataRequirement>;
  question: string;
  title: string;
}

export const defaultCannedQuestions: CannedQuestion[] = [
  {
    _id: 1,
    category: CannedQuestionCategory.Search,
    question: 'What is my best performing item?',
    title: 'Item performance',
  },
  {
    _id: 2,
    category: CannedQuestionCategory.SalesAlert,
    question: 'What are my sales this week?',
    title: 'Overall sales',
  },
  {
    _id: 3,
    category: CannedQuestionCategory.OrderPrice,
    question: 'Which items have decreased sales?',
    title: 'Sales decrease',
  },
  {
    _id: 4,
    category: CannedQuestionCategory.SalesAlert,
    question: 'Who is my top customer by sales volume?',
    title: 'Top customer',
  },
  {
    _id: 5,
    category: CannedQuestionCategory.OrderDate,
    question: 'Which accounts have placed orders today?',
    title: 'Orders',
  },
  {
    _id: 6,
    category: CannedQuestionCategory.OrderPrice,
    question: 'Which items have increased in sales?',
    title: 'Sales increase',
  },
  {
    _id: 7,
    category: CannedQuestionCategory.SalesAlert,
    question: 'Which customers are at risk of churn?',
    title: 'Churn risk',
  },
];

export interface CannedQuestionsContextType {
  error: string | null;
  loading: boolean;
  onQuestionClick: (questionId: number) => void;
  questions: CannedQuestion[];
}

interface Props {
  children?: React.ReactNode;
}

export const CannedQuestionsProvider: React.FC<Props> = ({ children }) => {
  const [questions, setQuestions] = useState<CannedQuestion[]>([]);
  const [clickedQuestionIds, setClickedQuestionIds] = useState<Set<number>>(
    new Set<number>(),
  );
  const appState = useRef(AppState.currentState);

  // Handle app state changes
  useEffect(() => {
    const subscription = AppState.addEventListener(
      'change',
      async (nextAppState) => {
        if (
          // App is coming from background or inactive state
          (appState.current === 'background' ||
            appState.current === 'inactive') &&
          nextAppState === 'active'
        ) {
          // Start new session when app becomes active
          await startNewSession();
        }
        appState.current = nextAppState;
      },
    );

    // Initialize first session
    startNewSession();

    return () => {
      subscription.remove();
    };
  }, []);

  // Handle active questions after added clicked question
  useEffect(() => {
    setQuestions((prevQuestions) => {
      return prevQuestions
        .filter((q) => !clickedQuestionIds.has(q._id))
        .slice(0, MAX_CANNED_QUESTIONS);
    });
  }, [clickedQuestionIds]);

  const startNewSession = () => {
    // Clear clicked questions
    setClickedQuestionIds(new Set());

    // Randomize questions
    setQuestions([...defaultCannedQuestions].sort(() => Math.random() - 0.5));
  };

  const handleQuestionClick = (questionId: number) => {
    setClickedQuestionIds((prev) => new Set(prev).add(questionId));
  };

  return (
    <CannedQuestionsContext.Provider
      value={{
        error: null,
        loading: false,
        onQuestionClick: handleQuestionClick,
        questions,
      }}
    >
      {children}
    </CannedQuestionsContext.Provider>
  );
};

export const CannedQuestionsContext =
  createContext<CannedQuestionsContextType | null>(null);

export const useCannedQuestionsContext = () => {
  const context = useContext(CannedQuestionsContext);

  if (!context) {
    throw new Error(
      'useCannedQuestionsContext requires a CannedQuestionsProvider',
    );
  }

  return context;
};
